import { type FC, useState } from 'react';
import {
  ChallengeRequestScreenState,
  type SCAOperation,
  ScaOperationStartingScreen,
} from '@shinetools/sca-operations';

import { type ChallengeWithStatus } from '__generated__/GQL';
import SunshineModal from 'components/_core/SunshineModal';
import ErrorView from 'components/ErrorView';
import ChallengeRequestApproved from 'features/StrongAuthentication/components/ChallengeRequestApproved';
import ChallengeRequestDeclined from 'features/StrongAuthentication/components/ChallengeRequestDeclined';
import ChallengeRequestExpired from 'features/StrongAuthentication/components/ChallengeRequestExpired';
import ChallengeRequestInfo from 'features/StrongAuthentication/components/ChallengeRequestInfo';
import ChallengeRequestPasscode from 'features/StrongAuthentication/components/ChallengeRequestPasscode';

import ChallengeRequestConfirm from '../components/ChallengeRequestConfirm';
import ChallengeRequestError from '../components/ChallengeRequestError';
import ChallengeRequestFraudRisk from '../components/ChallengeRequestFraudRisk';
import ChallengeRequestPhoneCall from '../components/ChallengeRequestPhoneCall';

export interface ChallengeRequestProps {
  challenge: ChallengeWithStatus | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ChallengeRequest: FC<ChallengeRequestProps> = ({
  challenge,
  isOpen,
  onClose,
}) => {
  const startingScreen =
    ScaOperationStartingScreen[challenge?.operation as SCAOperation] ??
    ChallengeRequestScreenState.PhoneCall;
  const [state, setState] =
    useState<ChallengeRequestScreenState>(startingScreen);

  const moveState = (newState: ChallengeRequestScreenState) => {
    setState(newState);
  };

  const close = () => {
    challenge = null;
    setState(ChallengeRequestScreenState.PhoneCall);
    onClose();
  };

  if (!challenge) {
    return null;
  }

  const { challengeId, operation, originDeviceId, userId } = challenge;

  const getComponent = () => {
    switch (state) {
      case ChallengeRequestScreenState.PhoneCall:
        return <ChallengeRequestPhoneCall moveState={moveState} />;
      case ChallengeRequestScreenState.FraudRisk:
        return (
          <ChallengeRequestFraudRisk
            challengeId={challengeId}
            onClose={close}
            operation={operation}
            userId={userId}
          />
        );
      case ChallengeRequestScreenState.Confirm:
        return <ChallengeRequestConfirm moveState={moveState} />;
      case ChallengeRequestScreenState.Info:
        return (
          <ChallengeRequestInfo
            challengeId={challengeId}
            moveState={moveState}
            operation={operation}
            originDeviceId={originDeviceId}
            payload={{}}
            userId={userId}
          />
        );
      case ChallengeRequestScreenState.Passcode:
        return (
          <ChallengeRequestPasscode
            challengeId={challengeId}
            moveState={moveState}
            operation={operation}
            originDeviceId={originDeviceId}
            payload={{}}
            userId={userId}
          />
        );

      case ChallengeRequestScreenState.Approved:
        return (
          <ChallengeRequestApproved
            challengeId={challengeId}
            moveState={moveState}
            onClose={close}
            operation={operation}
            userId={userId}
          />
        );
      case ChallengeRequestScreenState.Declined:
        return (
          <ChallengeRequestDeclined
            challengeId={challengeId}
            moveState={moveState}
            onClose={close}
            operation={operation}
            userId={userId}
          />
        );
      case ChallengeRequestScreenState.Expired:
        return <ChallengeRequestExpired onClose={close} />;
      case ChallengeRequestScreenState.Error:
        return <ChallengeRequestError onClose={close} />;
      default:
        return <ErrorView />;
    }
  };

  return (
    <SunshineModal
      closeOnEsc={false}
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={close}
    >
      <SunshineModal.Header />
      {getComponent()}
    </SunshineModal>
  );
};
